import clsx from "clsx";
import { overlay } from "overlay-kit";
import { type FC, type MouseEvent, type SyntheticEvent, useState } from "react";

import type { OEmbedType } from "../../../../shared/types/oembed";
import type { OEmbedDataType } from "../../../../shared/types/worksheet";

import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleExclamationIcon } from "mds/icons";
import { fetchOEmbed } from "utils/oembed";

type OEmbedModalProps = {
  onClose?: () => void;
  onConfirm: (oEmbed: OEmbedDataType) => void;
  showEmbedPageLabel?: boolean;
  url?: string;
  invalid?: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const showOEmbedModal = (url: string, showEmbedPageLabel = false) => {
  return overlay.openAsync<OEmbedDataType | false>(({ isOpen, close }) => (
    <Dialog open={isOpen} title={t("fields.embed.title")} onClose={() => close(false)}>
      <OEmbedModal
        showEmbedPageLabel={showEmbedPageLabel}
        url={url}
        onClose={() => close(false)}
        onConfirm={(data) => close(data)}
      />
    </Dialog>
  ));
};

export const OEmbedModal: FC<OEmbedModalProps> = ({
  onClose,
  onConfirm,
  url: initialUrl = "",
  showEmbedPageLabel = false,
  invalid = false,
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [invalidUrl, setInvalidUrl] = useState(invalid);
  const [unauthorized, setUnauthorized] = useState(false);

  const labelKey = showEmbedPageLabel ? "fields.embed.embed_page_label" : "fields.embed.label";

  const onChangeUrl = (newUrl: string) => {
    setUrl(newUrl);
    setInvalidUrl(false);
    setUnauthorized(false);
  };

  const onSubmit = async (event: SyntheticEvent<HTMLFormElement> | MouseEvent) => {
    event.preventDefault();

    if (!url) {
      setInvalidUrl(true);
      return;
    }

    let oEmbedData: OEmbedType | undefined;
    try {
      const { embed, errorCode } = await fetchOEmbed(url);

      if (errorCode === "401") {
        setUnauthorized(true);
        return;
      }

      // Any other error code will be treated as an invalid URL and oEmbedData will be undefined
      oEmbedData = embed;
    } catch (e) {
      console.error(e);
    }

    if (oEmbedData) {
      // This will close the modal
      onConfirm({ url, o: oEmbedData });
    }

    setInvalidUrl(true);
  };

  const hasError = unauthorized || invalidUrl;

  return (
    <form className="body-s text-black-tint-10" onSubmit={onSubmit}>
      <div className="flex w-full flex-col items-start gap-2">
        {/* Need custom label here so we can connect it to the input field */}
        <label className="font-medium" htmlFor="url">
          {t(labelKey)}
        </label>

        <TextField
          className={clsx("w-[350px] border-black-tint-90", { "bg-orange-tint-90": hasError })}
          id="url"
          placeholder="https://www.youtube.com/"
          readOnly={false}
          value={url}
          wrapperClassName="flex justify-center items-center grow"
          isEditing
          onChange={(e) => onChangeUrl(e.target.value)}
        />

        <div
          className={clsx("flex items-center gap-2 text-black-tint-20", {
            hidden: !hasError,
          })}
        >
          <CircleExclamationIcon className="text-orange" size="xs" />
          {unauthorized ? t("fields.embed.unauthorized") : t("fields.embed.invalid_url")}
        </div>
      </div>

      <div className="mt-8 flex w-full items-center justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={url.length === 0 || hasError}
          kind="accent"
          size="m"
          title={t("fields.embed.embed_video")}
          type="submit"
        >
          {t("fields.embed.embed_video")}
        </SquareButton>
      </div>
    </form>
  );
};
